import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import axios from 'axios';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const EventCostEstimator = ({ eventData, categories = [], onCostUpdate }) => {
  const user = useSelector(state => state.user.data);
  const { t } = useTranslation();
  const history = useHistory();
  const [costs, setCosts] = useState({
    creationFee: 0,
    totalCost: 0
  });
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch user's G-Coins balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!user?.id) return;
      
      try {
        const response = await axios.get(`${ENV_CONFIG.WALLET_BALANCE_ENDPOINT}/${user.id}`, {
          withCredentials: true
        });
        setUserBalance(response.data.balance || 0);
      } catch (error) {
        console.error('❌ Error fetching balance:', error);
        setUserBalance(0);
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [user?.id]);

  // Calculate costs based on event data
  useEffect(() => {
    const calculateCosts = () => {
      const selectedCategory = categories.find(c => String(c.id) === String(eventData.categoryId));
      const rawFee = selectedCategory?.creationGcoinPrice ?? 0;
      const creationFee = Number.isFinite(Number(rawFee)) ? Math.max(0, Number(rawFee)) : 0;
      const totalCost = creationFee;

      const newCosts = {
        creationFee,
        totalCost
      };

      setCosts(newCosts);
      
      // Notify parent component
      if (onCostUpdate) {
        onCostUpdate(totalCost, userBalance >= totalCost);
      }
    };

    calculateCosts();
  }, [eventData, categories, userBalance, onCostUpdate]);

  if (loading) {
    return (
      <EstimatorContainer>
        <EstimatorTitle>💰 {t('createevent.creation_fee_title', 'Event Creation Fee')}</EstimatorTitle>
        <LoadingText>{t('component.loading_balance', 'Loading balance...')}</LoadingText>
      </EstimatorContainer>
    );
  }

  const canAfford = userBalance >= costs.totalCost;

  const redirectToGcoinStore = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    try {
      localStorage.setItem('postGcoinPurchaseRedirect', returnTo);
    } catch (_) {
      // ignore
    }
    toast.error(
      t(
        'eventWatch.insufficientBalanceRedirect',
        'Insufficient balance. Redirecting to buy G-Coins...'
      )
    );
    const params = new URLSearchParams({
      redirect: returnTo,
      reason: 'insufficient_balance'
    });
    history.push(`/gcoin-store?${params.toString()}`);
  };

  return (
    <EstimatorContainer>
      <EstimatorTitle>💰 {t('createevent.creation_fee_title', 'Event Creation Fee')}</EstimatorTitle>
      
      <CostBreakdown>
        <CostItem>
          <CostLabel>{t('createevent.creation_fee', 'Creation fee')}:</CostLabel>
          <CostValue>{costs.creationFee} G-Coins</CostValue>
        </CostItem>
      </CostBreakdown>

      <TotalCost canAfford={canAfford}>
        <TotalLabel>{t('common.total', 'Total')}:</TotalLabel>
        <TotalValue>{costs.totalCost} G-Coins</TotalValue>
      </TotalCost>

      <BalanceInfo canAfford={canAfford}>
        <BalanceLabel>{t('component.your_balance', 'Your Balance:')}</BalanceLabel>
        <BalanceValue canAfford={canAfford}>{userBalance} G-Coins</BalanceValue>
      </BalanceInfo>

      {!canAfford && (
        <InsufficientFunds>
          ⚠️ {t('createevent.insufficient_funds', 'Insufficient funds!')} {t('createevent.you_need_more', 'You need')} {costs.totalCost - userBalance} {t('createevent.more_gcoins', 'more G-Coins')}.
          <RechargeButton onClick={redirectToGcoinStore}>
            {t('nav.buyGCoins', 'Buy G-Coins')}
          </RechargeButton>
        </InsufficientFunds>
      )}

      {canAfford && (
        <SufficientFunds>
          ✅ {t('createevent.sufficient_funds_publish', 'You have sufficient funds to publish this event!')}
        </SufficientFunds>
      )}
    </EstimatorContainer>
  );
};

// Styled Components
const EstimatorContainer = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
`;

const EstimatorTitle = styled.h3`
  color: ${props => props.theme.textColor};
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
`;

const LoadingText = styled.p`
  color: ${props => props.theme.textSecondary};
  text-align: center;
  margin: 1rem 0;
`;

const CostBreakdown = styled.div`
  margin-bottom: 1rem;
`;

const CostItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.borderColor}20;
`;

const CostLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
`;

const CostValue = styled.span`
  color: ${props => props.theme.textColor};
  font-weight: 500;
`;

const TotalCost = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${props => props.canAfford ? props.theme.successBackground : props.theme.errorBackground}20;
  border-radius: 6px;
  margin: 1rem 0;
  border: 1px solid ${props => props.canAfford ? props.theme.successColor : props.theme.errorColor}40;
`;

const TotalLabel = styled.span`
  color: ${props => props.theme.textColor};
  font-weight: 600;
  font-size: 1.1rem;
`;

const TotalValue = styled.span`
  color: ${props => props.theme.primaryColor};
  font-weight: 700;
  font-size: 1.2rem;
`;

const BalanceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackground};
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const BalanceLabel = styled.span`
  color: ${props => props.theme.textSecondary};
`;

const BalanceValue = styled.span`
  color: ${props => props.canAfford ? props.theme.successColor : props.theme.errorColor};
  font-weight: 600;
`;

const InsufficientFunds = styled.div`
  background: ${props => props.theme.errorBackground}20;
  border: 1px solid ${props => props.theme.errorColor}40;
  border-radius: 6px;
  padding: 1rem;
  color: ${props => props.theme.errorColor};
  text-align: center;
`;

const SufficientFunds = styled.div`
  background: ${props => props.theme.successBackground}20;
  border: 1px solid ${props => props.theme.successColor}40;
  border-radius: 6px;
  padding: 1rem;
  color: ${props => props.theme.successColor};
  text-align: center;
  font-weight: 500;
`;

const RechargeButton = styled.button`
  background: ${props => props.theme.primaryColor};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  margin-top: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.theme.primaryColorHover};
  }
`;

export default EventCostEstimator;
